<template lang="pug">
.media(:class="[layout, 'gallery-'+gallery.length]" @keydown.esc="onEscape")
  Media.zoom-view(
    v-if="showZoom"
    layout="zoomed"
    :gallery="gallery"
    @close="showZoom = false"
    v-model="focused"
    :product="product"
  )
    template(#overlay="{ idx, img }")
      slot(v-if="$slots.zoomoverlay" name="zoomoverlay" :idx="idx" :img="img")
      slot(v-else-if="$slots.overlay" name="overlay" :idx="idx" :img="img")
  button.a11y.close-zoom(v-if="layout === 'zoomed'" ref="closeBtn" type="button" :aria-label="__('Close gallery')" @click="$emit('close')")
    Icon(name="x" fill="#000")
  ImageFlag.product-media-flag(:product="product" :imageFlagMedia="imageFlagMedia")
  .thumbs(v-if="gallery.length > 1")
    slot(name="thumbs")
      MediaThumbs.thumbs-gallery(
        :gallery="gallery"
        v-model="focused"
        :product="product"
      )
  .gallery-wrap(:class="gallery.length > 1 && 'has-thumbs'")
    slot(:focused="focused")
      .gallery-placeholder(v-if="!gallery.length")
      MediaImages.gallery(
        v-else
        :gallery="gallery"
        :layout="layout"
        v-model="focused"
        :product="product"
        ref="gallery"
        @click="clickImage($event)"
      )
        // Allow the PDP to add overlays to the images
        template(#overlay="data")
          slot(v-if="$slots.overlay" name="overlay" :idx="data.idx" :img="data.img")
        // TO-DO: investigate putting these in client-only component
        ArrowNav.pdp-controls(
          v-model="focused"
          :max="gallery.length"
          prevLabel="Previous image"
          nextLabel="Next image"
          v-if="layout !== 'zoomed' && gallery.length > 1"
        )
</template>
<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRogue } from '../../../../js/rogue'
import ArrowNav from '../../ArrowNav.vue'
import MediaThumbs from './MediaThumbs.vue'

const props = defineProps({
  gallery: {
    type    : Array,
    default : () => [],
  },
  layout: {
    type     : String,
    required : false,
  },
  modelValue: {
    type    : Number,
    default : 0,
  },
  product: {
    type    : Object,
    default : () => ({}),
  },
  enableZoom: {
    type    : Boolean,
    default : true,
  },
  imageFlagMedia: {
    type     : String,
    default  : '',
    required : false,
  },
})

const emit = defineEmits<{
  close: [],
  'update:modelValue': [value: number]
}>()

const rogue = useRogue()

const showZoom = ref(false)
const focused = ref(props.modelValue)
const closeBtn = useTemplateRef<HTMLButtonElement>('closeBtn')

// Where keyboard focus was when the zoom view opened, so closing it returns the user there
let returnFocusTo: HTMLElement | null = null

watch(showZoom, (open) => {
  if (open) {
    returnFocusTo = document.activeElement as HTMLElement | null
  } else {
    const el = returnFocusTo
    returnFocusTo = null
    nextTick(() => el?.focus())
  }
})

function onEscape() {
  if (props.layout === 'zoomed') {
    emit('close')
  }
}

watch(focused, (newVal) => {
  if (props.modelValue !== newVal) {
    emit('update:modelValue', newVal)
  }
})

watch(() => props.modelValue, (newVal) => {
  if (focused.value !== newVal) {
    focused.value = newVal
  }
})

function clickImage(index: number) {
  if (props.enableZoom) {
    focused.value = index
    if (props.layout !== 'zoomed') {
      showZoom.value = true
    }
  }
  rogue?.track?.track({type: 'custom', event: 'Gallery Image Click', data: { name: props.product.name, sku: props.product.sku, imageIdx: index }})
}

function loadQueryImage() {
  const imgNum = parseInt((rogue?.router?.currentRoute.value.query.img || '') as string, 10)
  if (typeof imgNum === 'number' && props.gallery.length > 1 && imgNum <= props.gallery.length && imgNum !== 0) {
    focused.value = imgNum - 1
    if (props.layout !== 'zoomed') {
      showZoom.value = true
    }
  }
}

onMounted(() => {
  loadQueryImage()
  // The zoom view covers the page, so move keyboard focus into it
  if (props.layout === 'zoomed') {
    nextTick(() => closeBtn.value?.focus())
  }
})
</script>
<style lang="scss">
// Hide hero image from the gallery on desktop
@include all-desktop() {
  // If this product only has one gallery item, hide the full gallery. Otherwise hide the first gallery entry
  .product-pg.herostyle-useFirst, .product-pg.herostyle-replaceFirst {
    .gallery-1, .product-media.gallery:not(.pm-zoomed) .media-wrap:first-child {
      display: none;
    }
  }
}
</style>
<style lang="scss" scoped>
.media {
  display: flex;
  position: relative;
  width: 100%;

  .product-media-flag.flag-container {
    position: absolute;
    right: 0px;
    top: 0px;
    z-index: 1;

    @include large() {
      right: 0px;
    }

    @include not-desktop() {
      right: $space-s;
      top: $space-xxs;
    }
  }
}

.gallery-wrap {
  position: relative;
  pointer-events: none;
  width: 100%;
}

.gallery-placeholder {
  width: 100%;
  aspect-ratio: 1;
  background-color: $color-gray-x-light;
}

.gallery-wrap > .gallery {
  pointer-events: all;
  width: 100%;
}

.media.zoomed {
  position: fixed;
  background-color: $color-white;
  z-index: 100;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overscroll-behavior: contain;

  .gallery-wrap, .gallery-wrap .gallery {
    height: 100%;
  }
}

.zoom-view {
  z-index: 1;
}

.thumbs:not(.thumbs-gallery) {
  padding: $space-m 0; // make room for the arrow navs
}

.pdp-controls {
  z-index: 1;
}

.close-zoom {
  box-sizing: content-box;
  position: absolute;
  top: $space-s;
  right: $space-s;
  z-index: 2;
  width: 32px;
  cursor: pointer;
  height: 32px;
  padding: 8px;
  background-color: hsla(0, 0%, 100%, .8);
  border-radius: 3px;
}

@include not-desktop() {
  .media .gallery {
    width: 100%;
  }

  .media.zoomed {
    display: flex;
    flex-direction: column-reverse;
    height: 100%;

    .gallery {
      flex-grow: 1;
      position: absolute;
      top: 0;
      bottom: 100px;
      left: 0;
      width: 100%;
    }

    :deep(.media-images) {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .thumbs {
      display: flex;
      overflow-y: hidden;
      overflow-x: auto;
      width: 100%;
      height: 84px;
      flex-shrink: 0;
      min-height: 84px;
      padding: 0 $space-s;
      align-items: center;
      flex-direction: row;

      :deep(.img-container) {
        width: 50px;
        flex-shrink: 0;
      }
    }

    @include tablet() {
      .thumbs {
        height: 134px;
        min-height: 134px;

        :deep(.img-container) {
          width: 100px;
        }
      }
    }
  }
}

@include all-desktop() {
  .media.hero .product-media-flag {
    display: none
  }
}

.thumbs:not(.thumbs-gallery) {
  position: absolute;

  @include not-desktop() {
    height: 100px;
    max-width: 100%;
    left: 0;
    right: 0;
    bottom: 20px;
  }

  @include tablet() {
    height: 150px;
  }

  @include all-desktop() {
    width: 150px;
    height: 100%;
    top: 0;
    bottom: 0;
  }
}

@include not-desktop() {
  .media.zoomed .thumbs:not(.thumbs-gallery) {
    position: relative;
  }
}

@include all-desktop() {
  .media.zoomed .thumbs:not(.thumbs-gallery) {
    position: fixed;
    z-index: $z-index-sticky;
  }
}

.gallery-wrap {
  flex-grow: 1;

  @include all-desktop() {
    padding-left: $size-xxxxxxl;
  }

  @include desktop() {
    padding-left: $size-xxxxl;
  }

  &.has-thumbs {
    @include not-desktop() {
      padding-bottom: 120px;
    }
     @include tablet() {
      padding-bottom: 150px;
    }
    @include all-desktop() {
      padding-left: 150px;
    }
  }
}

@include all-desktop() {
  .media.hero {
    > .thumbs {
      display: none;
    }

    > .gallery-wrap {
      padding-left: 0;
    }
  }
}
</style>
